import React from 'react';

const inputCls = "w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none";
const labelCls = "block text-[12px] font-semibold text-[#4A4B63] mb-1";

/**
 * Address + Contact + Notes sections grouped together.
 * Extracted from CompanyFormDialog (Vague 97.4).
 */
export default function CompanyContactSection({ values, set }) {
  return (
    <>
      <section>
        <h3 className="text-[11.5px] font-semibold text-[#6B6C85] mb-2 tracking-wide uppercase">Adresse du siège</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-3">
            <label className={labelCls}>Adresse complète</label>
            <input value={values.address} onChange={set('address')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Code postal</label>
            <input value={values.postal_code} onChange={set('postal_code')} className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Ville</label>
            <input value={values.city} onChange={set('city')} className={inputCls} />
          </div>
          <div className="col-span-3">
            <label className={labelCls}>Pays</label>
            <input value={values.pays || ''} onChange={set('pays')}
                   data-testid="company-pays"
                   className={inputCls} placeholder="France" />
            <span className="text-[11px] text-[#8E8FA5]">
              Il ne s&rsquo;imprime sur les documents que s&rsquo;il n&rsquo;est pas la France.
            </span>
          </div>
        </div>
      </section>

      {/* L'adresse de facturation, si elle diffère du siège.
          Un service comptable ailleurs, un cabinet qui reçoit pour son client,
          un siège à l'étranger qui facture en France. Laissée vide, c'est
          celle du siège qui s'imprime — les fiches existantes ne changent
          pas. Et une voie seule suffit à dire « c'est ailleurs » : exiger les
          quatre champs ferait facturer au mauvais endroit en silence. */}
      <section>
        <h3 className="text-[11.5px] font-semibold text-[#6B6C85] mb-2 tracking-wide uppercase">
          Adresse de facturation
        </h3>
        <p className="text-[11px] text-[#8E8FA5] -mt-1 mb-2">
          Seulement si elle diffère du siège.{' '}
          <strong>Laissée vide, c&rsquo;est celle du siège</strong> qui figure sur le
          devis et la facture.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-3">
            <label className={labelCls}>Adresse</label>
            <input value={values.facturation_adresse || ''}
                   onChange={set('facturation_adresse')}
                   data-testid="facturation-adresse"
                   className={inputCls} placeholder={values.address || '—'} />
          </div>
          <div>
            <label className={labelCls}>Code postal</label>
            <input value={values.facturation_code_postal || ''}
                   onChange={set('facturation_code_postal')}
                   className={inputCls} placeholder={values.postal_code || '—'} />
          </div>
          <div>
            <label className={labelCls}>Ville</label>
            <input value={values.facturation_ville || ''}
                   onChange={set('facturation_ville')}
                   className={inputCls} placeholder={values.city || '—'} />
          </div>
          <div>
            <label className={labelCls}>Pays</label>
            <input value={values.facturation_pays || ''}
                   onChange={set('facturation_pays')}
                   className={inputCls} placeholder={values.pays || 'France'} />
          </div>
        </div>
      </section>

      {/* Deux contacts, pas trois.
          La fiche n'en portait qu'un, et il recevait tout : le devis, la
          convention a signer, l'etude de besoin, la satisfaction **et** la
          facture. Impossible de savoir lequel de ces roles il portait.
          Le plan de formation commande la formation, donc il recoit ce qui la
          prepare et ce qui la conclut ; la facturation est le seul role qui
          diverge vraiment en pratique (compta@, un cabinet, Chorus). */}
      <section>
        <h3 className="text-[11.5px] font-semibold text-[#6B6C85] mb-2 tracking-wide uppercase">
          Contact — plan de formation
        </h3>
        <p className="text-[11px] text-[#8E8FA5] -mt-1 mb-2">
          Il reçoit le devis, la convention à signer, l&rsquo;étude de besoin, les
          convocations et le questionnaire de satisfaction.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Nom du contact</label>
            <input value={values.contact_name} onChange={set('contact_name')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={values.contact_email} onChange={set('contact_email')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Téléphone</label>
            <input value={values.contact_phone} onChange={set('contact_phone')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Site web</label>
            <input value={values.website} onChange={set('website')} className={inputCls} placeholder="https://…" />
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-[11.5px] font-semibold text-[#6B6C85] mb-2 tracking-wide uppercase">
          Contact — facturation
        </h3>
        <p className="text-[11px] text-[#8E8FA5] -mt-1 mb-2">
          Il reçoit les factures et les relances.{' '}
          <strong>Laissé vide, c&rsquo;est le contact du plan de formation qui les reçoit</strong> —
          ne le remplissez que s&rsquo;il diffère.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Nom du contact</label>
            <input value={values.facturation_nom || ''} onChange={set('facturation_nom')}
                   data-testid="facturation-nom"
                   className={inputCls} placeholder={values.contact_name || '—'} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" value={values.facturation_email || ''}
                   onChange={set('facturation_email')} data-testid="facturation-email"
                   className={inputCls} placeholder={values.contact_email || '—'} />
          </div>
        </div>
      </section>

      <section>
        <label className={labelCls}>Notes</label>
        <textarea
          value={values.notes}
          onChange={set('notes')}
          rows={3}
          className="w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] outline-none resize-none"
        />
      </section>
    </>
  );
}
